import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper
 * @property {HTMLElement} content
 * @property {HTMLElement[]} marqueeItems
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  #animation = null;
  #resizeObserver = null;
  #lastWidth = 0;
  #connected = false;
  #hovered = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.#connected) return;

    const firstItem = this.refs.content.firstElementChild;
    if (!firstItem) return;

    this.#connected = true;
    this.#lastWidth = this.getBoundingClientRect().width;

    this.#rebuild();

    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this);

    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('pointerleave', this.#handlePointerLeave);

    document.fonts?.ready.then(() => {
      if (this.#connected) this.#rebuild();
    });
  }

  disconnectedCallback() {
    this.#connected = false;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#handleResize.cancel();
    this.#slowDown.cancel();
    this.#animation?.cancel();
    this.#animation = null;
    this.#hovered = false;

    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);

    super.disconnectedCallback();
  }

  #handlePointerEnter = (event) => {
    if (
      event.pointerType !== 'mouse' ||
      !window.matchMedia('(hover: hover) and (pointer: fine)').matches
    ) {
      return;
    }

    this.#hovered = true;
    this.#slowDown();
  };

  #handlePointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;

    this.#hovered = false;
    this.#speedUp();
  };

  #slowDown = debounce(() => {
    if (!this.#connected || !this.#hovered) return;

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: animation.playbackRate,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#animation?.cancel();
    this.#animation = null;

    if (animation.playbackRate === 1) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: animation.playbackRate,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  #handleResize = debounce(() => {
    if (!this.#connected) return;

    const width = this.getBoundingClientRect().width;
    if (width <= 0 || Math.abs(width - this.#lastWidth) < 1) return;

    this.#lastWidth = width;
    this.#rebuild();
  }, 250);

  #rebuild() {
    const { content, wrapper } = this.refs;
    const firstItem = content.firstElementChild;
    const width = this.getBoundingClientRect().width;

    if (!firstItem || width <= 0) return;

    const itemWidth = firstItem.getBoundingClientRect().width;
    if (itemWidth <= 0) return;

    const contentStyles = getComputedStyle(content);
    const parsedGap = parseFloat(contentStyles.columnGap);
    const gap = Number.isFinite(parsedGap) ? parsedGap : 0;

    // Each group must cover the visible marquee width.
    const requiredCopies = Math.max(
      1,
      Math.ceil((width + gap) / (itemWidth + gap))
    );

    this.#slowDown.cancel();
    this.#animation?.cancel();
    this.#animation = null;

    // Preserve the original editable group and rebuild only its copies.
    while (content.children.length > requiredCopies) {
      content.lastElementChild.remove();
    }

    while (content.children.length < requiredCopies) {
      const copy = firstItem.cloneNode(true);
      this.#prepareClone(copy);
      content.appendChild(copy);
    }

    // Keep exactly one duplicate of the complete content group.
    for (const child of Array.from(wrapper.children)) {
      if (child !== content) child.remove();
    }

    const clone = content.cloneNode(true);
    this.#prepareClone(clone);
    wrapper.appendChild(clone);

    const rawSpeedFactor = Number(this.getAttribute('data-speed-factor'));
    const speedFactor =
      Number.isFinite(rawSpeedFactor) && rawSpeedFactor > 0
        ? rawSpeedFactor
        : 25;

    const speed = Math.sqrt(requiredCopies) * speedFactor;
    this.style.setProperty('--marquee-speed', `${speed}s`);

    for (const animation of wrapper.getAnimations()) {
      animation.updatePlaybackRate(this.#hovered ? 0 : 1);
      animation.currentTime = 0;
    }
  }

  #prepareClone(clone) {
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.removeAttribute('ref');
    clone.removeAttribute('id');

    for (const element of clone.querySelectorAll('[ref], [id]')) {
      element.removeAttribute('ref');
      element.removeAttribute('id');
    }
  }
}

/**
 * Animate a numeric value smoothly.
 */
function animateValue({
  from,
  to,
  duration,
  onUpdate,
  easing = (t) => t * t * (3 - 2 * t),
  onComplete,
}) {
  const startTime = performance.now();
  let cancelled = false;
  let frameId = 0;
  let currentValue = from;

  function animate(currentTime) {
    if (cancelled) return;

    const progress = Math.min((currentTime - startTime) / duration, 1);
    currentValue = from + (to - from) * easing(progress);
    onUpdate(currentValue);

    if (progress < 1) {
      frameId = requestAnimationFrame(animate);
    } else {
      onComplete?.();
    }
  }

  frameId = requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
      cancelAnimationFrame(frameId);
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
